import "@assets/css/bootstrap-grid.css";
import { Spinner } from "@components/ui/spinner";

import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import {
  makeSlug,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  BlogCategories,
  BlogCategoryFormData,
} from "@utils/interface/form.schema";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

const CategoryForm: React.FC = () => {
  const { searchParams } = useHandleAction({});
  const pathname = usePathname();
  const modalItem = searchParams.get("modal-item");
  const modalMode = searchParams.get("modal");
  const {
    handleSubmit,
    control,
    setValue,
    // watch,
    // setError,
    // clearErrors,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(BlogCategories),
    defaultValues: {
      name: modalMode === "add" ? (searchParams.get("search") as string) : "",
    },
  });

  const { data: fetchData, isPending: fetchPending } = useListData<any>({
    endpoint: endpoint["blog-category"],
    key: `${pathname}-${modalItem}`,
    enabled: modalItem ? true : false,
    id: modalItem || "",
    window: false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();
  const { setIsFormPending } = useSelectionStore();
  /* set default value */
  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      console.log("firstResult", firstResult);
      if (firstResult) {
        if (searchParams.get("page") === "add") {
          return reset({});
        } else {
          reset(() => ({
            ...firstResult,
          }));
        }
      }
    }
  }, [fetchData?.data, reset]);

  const queryClient = useQueryClient();
  const { handleAction } = useHandleAction({});

  const handleMutate = (data: BlogCategoryFormData) => {
    setIsFormPending(true);

    data = {
      ...data,
    };
    if (modalItem) {
      data = {
        ...data,
        id: modalItem,
      };

      // if (!(data.media instanceof File || data.media instanceof Blob))
      //   //@ts-ignore
      //   delete data.media;
    }
    return {
      data,
      endpoint: endpoint["blog-category"],
      method: modalItem ? METHODS.PUT : METHODS.POST,
      id: modalItem,
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      setIsFormPending(false);
      showToast({
        description: `Category has been  ${
          status === 200 ? "Updated" : "Created"
        }`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "modal-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    console.log(error, "error00");
    setIsFormPending(false);
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    throwErrorAlert(error);
  };

  const { mutate, isPending } = usePostData();
  console.log("errors", errors);
  const onSubmit = (data: BlogCategoryFormData) => {
    //Add Mutation Data
    mutate(handleMutate(data as any), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = useMemo(
    () => [
      {
        name: "name",
        label: "Category Name",
        colSpan: 10,
        type: "text",
        placeholder: "Enter Category Name",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "is_show",
        label: "Visbility",
        colSpan: 2,
        type: "switch",
        placeholder: "",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] mt-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "media",
        label: "Upload Image",
        colSpan: 12,
        type: "cropper",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
      },
      {
        type: "submit",
        value: modalItem ? "Update Category" : "Create Category",
        className: "w-full h-[47px] mt-[32px]",
        colSpan: 12,
        variant: "default",
        endIcon: isPending ? <Spinner /> : null,
        disabled: isPending ? true : false,
        visable: true,
      },
    ],
    [control, errors, setValue, isPending]
  );

  if (fetchPending && modalItem)
    return (
      <>
        <SkeletonTwo />
      </>
    );

  return (
    <>
      <form
        id={pathname}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan}  col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default CategoryForm;
